// ─────────────────────────────────────────────────────────────────────────────
// GENERATED FILE — DO NOT EDIT HERE.
//
// Source of truth: invits-app-new/shared/analyticsEvents.js
// Regenerate:     npm run sync:shared   (from invits-app-new)
//
// Editing this copy reintroduces exactly the app/web divergence that shared/
// exists to eliminate. Change the source, then re-run the sync.
// ─────────────────────────────────────────────────────────────────────────────

// shared/analyticsEvents.js
//
// The product events the app and the website record (see
// supabase/migrations/20260927100000_app_events.sql), in one place so the two
// clients use the same names and Invits Office's funnels line up across them.
//
// Names are snake_case, [a-z][a-z0-9_]{1,47} - the database rejects anything
// else. Properties are small and never personal: an event type, a cover kind,
// a reason code. Never a title, a message, a name, an email or a number.

export const EV = {
  // ── Session ──────────────────────────────────────────────────────────────
  APP_OPENED: 'app_opened',                 // a new session (app) / page load (web)

  // ── Signup and sign-in ───────────────────────────────────────────────────
  WELCOME_VIEWED: 'welcome_viewed',         // the signed-out landing screen
  SIGNUP_STARTED: 'signup_started',         // { method: email | phone } - code requested
  SIGNUP_CODE_VERIFIED: 'signup_code_verified', // { method }
  SIGNUP_COMPLETED: 'signup_completed',     // { method: email | phone | google, source? }
  SIGNUP_FAILED: 'signup_failed',           // { step, code }
  LOGIN_COMPLETED: 'login_completed',       // { method }
  LOGIN_FAILED: 'login_failed',             // { method, code }
  PROFILE_SETUP_VIEWED: 'profile_setup_viewed',
  PROFILE_SETUP_COMPLETED: 'profile_setup_completed',

  // ── Home ─────────────────────────────────────────────────────────────────
  HOME_VIEWED: 'home_viewed',

  // ── Creating an invitation ───────────────────────────────────────────────
  CREATE_OPENED: 'create_opened',           // { mode: new | edit }
  COVER_PICKER_OPENED: 'cover_picker_opened',
  COVER_SELECTED: 'cover_selected',         // { type: image | template | gif | pdf | youtube }
  TITLE_ENTERED: 'title_entered',           // first non-empty title, once per editor
  EVENT_TYPE_SELECTED: 'event_type_selected', // { event_type }
  DATE_SET: 'date_set',                     // once per editor
  LOCATION_SET: 'location_set',             // once per editor
  CREATE_TAB_VIEWED: 'create_tab_viewed',   // { tab: basic | rsvp | passes | more }
  CREATE_STEP_BLOCKED: 'create_step_blocked', // { from, to } - validation stopped a tab change
  PREVIEW_OPENED: 'preview_opened',
  PUBLISH_TAPPED: 'publish_tapped',
  PUBLISH_BLOCKED: 'publish_blocked',       // { reason: validation | verification_phone | verification_email | terms | error, field? }
  INVITATION_PUBLISHED: 'invitation_published', // { event_type, privacy, pass_type, cover_type }
  DRAFT_SAVED: 'draft_saved',
  CREATE_DISCARDED: 'create_discarded',     // { had_title, tab }

  // ── First-invitation tour ────────────────────────────────────────────────
  // Its invitations also carry { tour: true } on invitation_published, and
  // events.created_via_tour in the database.
  TOUR_OFFERED: 'tour_offered',             // the "take a tour?" prompt was shown
  TOUR_STARTED: 'tour_started',
  TOUR_STEP: 'tour_step',                   // { step } - reached a step
  TOUR_SKIPPED: 'tour_skipped',             // { step } - left the tour there
  TOUR_COMPLETED: 'tour_completed',         // published through the tour

  // ── Ratings ──────────────────────────────────────────────────────────────
  REVIEW_REQUESTED: 'review_requested',     // { reason } - the store's rating sheet was asked for (once per install)

  // ── Effects ──────────────────────────────────────────────────────────────
  EFFECT_PLAYBACK_FAILED: 'effect_playback_failed', // { effect, reason: autoplay-blocked | no-webgl | media-error-N | play-*, fallback: played the Low Power version }

  // ── Notifications prompt ─────────────────────────────────────────────────
  NOTIF_PROMPT_SHOWN: 'notif_prompt_shown',       // { via: ask | settings } - notifications are off; asked to turn them on
  NOTIF_PROMPT_ANSWERED: 'notif_prompt_answered', // { via, result: granted | denied | settings_opened | not_now }

  // ── Daily nudges (local notifications) ───────────────────────────────────
  NUDGE_OPENED: 'nudge_opened',             // { nudge } - a nudge was tapped

  // ── After publishing ─────────────────────────────────────────────────────
  SHARE_OPENED: 'share_opened',             // { what: invitation | card }
  SHARED: 'shared',                         // { what, channel }

  // ── Guests ───────────────────────────────────────────────────────────────
  INVITATION_VIEWED: 'invitation_viewed',   // { as: host | guest | signed_out }
  RSVP_TAPPED: 'rsvp_tapped',               // { status, signed_in }
  RSVP_SIGNUP_STARTED: 'rsvp_signup_started', // { method } - web RSVP popup
  RSVP_SIGNUP_COMPLETED: 'rsvp_signup_completed', // { method }
  RSVP_SUBMITTED: 'rsvp_submitted',         // { status }

  // ── Cards ────────────────────────────────────────────────────────────────
  CARD_CREATE_OPENED: 'card_create_opened',
  CARD_PUBLISHED: 'card_published',
  CARD_VIEWED: 'card_viewed',               // { as: creator | recipient }
  CARD_OPENED: 'card_opened',               // the envelope tapped open
};

/** Keeps a property bag small and flat: primitives only, short strings, ≤ 12 keys. */
export function cleanProps(props) {
  if (!props || typeof props !== 'object') return {};
  const out = {};
  let n = 0;
  for (const [k, v] of Object.entries(props)) {
    if (n >= 12) break;
    if (!/^[a-z][a-z0-9_]{0,31}$/.test(k)) continue;
    if (v === null || v === undefined) continue;
    if (typeof v === 'boolean' || (typeof v === 'number' && Number.isFinite(v))) out[k] = v;
    else if (typeof v === 'string') out[k] = v.slice(0, 64);
    else continue;
    n += 1;
  }
  return out;
}

/** True for a name the database will accept. */
export const isEventName = (name) => typeof name === 'string' && /^[a-z][a-z0-9_]{1,47}$/.test(name);

/** How long without activity before the next event starts a new session. */
export const SESSION_GAP_MS = 30 * 60 * 1000;
